import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { TrendingUp, FileText, Loader2, CheckCircle2, Lock, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRICE_FMT = { gbp: '£', inr: '₹', cad: 'C$' };

/**
 * GrowthAnalysisPanel — per-city paid AI market-intelligence CTA.
 * Drops onto every LocationLandingPage (city or sector slug).
 *
 * Flow:
 *   free teaser (first 500 chars)  →  Stripe checkout  →  /growth-report/:slug
 */
const GrowthAnalysisPanel = ({ slug }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [purchased, setPurchased] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/growth/preview/${slug}`);
        if (!cancelled) setPreview(r.data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Check if the current user already owns the report
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const my = await axios.get(`${API_URL}/api/growth/purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && my.data?.items?.some((p) => p.slug === slug)) {
          setPurchased(true);
        }
      } catch {
        /* not logged in or API error — silent */
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleBuy = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = `/login?next=/properties-in/${slug}`;
      return;
    }
    setBuying(true);
    try {
      const r = await axios.post(`${API_URL}/api/growth/checkout`, {
        slug,
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data?.already_purchased) {
        setPurchased(true);
        toast.success('You already own this report — opening now.');
        window.location.href = `/growth-report/${slug}`;
      } else if (r.data?.url) {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="luxury-card p-6 my-6 flex items-center gap-3 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Loading AI Growth Analysis…
      </div>
    );
  }
  if (!preview) return null;

  const sym = PRICE_FMT[(preview.price?.currency || 'gbp')] || '£';
  const amount = preview.price?.amount;
  const priceLabel = amount && amount % 1 === 0
    ? `${sym}${amount.toLocaleString()}`
    : `${sym}${amount?.toLocaleString()}`;

  return (
    <div className="luxury-card p-6 sm:p-8 my-8 bg-gradient-to-br from-[#1F3D2B] via-[#2A4F39] to-[#1F3D2B] text-white relative overflow-hidden" data-testid={`growth-analysis-${slug}`}>
      {/* soft grid pattern */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '22px 22px' }} />

      <div className="relative flex items-start justify-between flex-wrap gap-4 mb-4">
        <div>
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-semibold">AI Growth Analysis · {preview.country}</span>
          <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl mt-1 leading-tight">
            Professional-grade intelligence on <span className="italic text-[#F0A875]">{preview.name}</span>
          </h3>
          <p className="text-sm text-white/70 mt-1">{preview.tagline}</p>
        </div>
      </div>

      <ul className="relative text-sm text-white/85 grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
        {(preview.sample_headings || []).map((h, i) => (
          <li key={h} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-[#F0A875] mt-0.5 flex-shrink-0" />
            <span>{i + 1}. {h}</span>
          </li>
        ))}
      </ul>

      {preview.teaser && (
        <div className="relative bg-black/25 border border-white/10 rounded p-4 text-xs text-white/70 leading-relaxed mb-4 font-mono max-h-32 overflow-hidden">
          {preview.teaser}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#1F3D2B] to-transparent" />
        </div>
      )}

      <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        {purchased ? (
          <a
            href={`/growth-report/${slug}`}
            className="inline-flex items-center gap-2 bg-[#F0A875] text-[#1F3D2B] hover:bg-[#E07A3A] hover:text-white tracking-widest text-sm px-7 py-4 font-bold uppercase transition-colors"
            data-testid={`growth-download-${slug}`}
          >
            <Download className="h-4 w-4" />Read my report
          </a>
        ) : (
          <Button
            onClick={handleBuy}
            disabled={buying}
            className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-7 py-5 font-bold uppercase"
            data-testid={`growth-buy-${slug}`}
          >
            {buying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            Unlock for {priceLabel}
          </Button>
        )}
        <div className="text-[11px] text-white/50 flex items-center gap-1.5">
          <TrendingUp className="h-3 w-3" />
          90-day unlimited download · generated by Pulse Engine
        </div>
      </div>

      <p className="relative text-[10px] text-white/40 mt-4">
        Matchmaking Service Fee — Shopperden is an AI Real Estate Network, not an estate agent or brokerage. Report is informational; always seek independent professional advice.
      </p>
    </div>
  );
};

export default GrowthAnalysisPanel;
